import { Box } from '@material-ui/core';
import { LenderApprovalStatus } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { LenderSelectedData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { BnplPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/PaymentMethodDetail/BnplPaymentMethodDetailEntity';
import { useBnplPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/hooks/useBnplPaymentIntent';
import { DistributionDialog } from '@/components/shared/components/DistributionDialog/DistributionDialog';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface LenderSelectedProps {
  paymentMethodDetailEntity: BnplPaymentMethodDetailEntity;
  lenderSelectedData: LenderSelectedData | null;
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
  onClose?: () => void;
}

export const LenderSelected = ({
  paymentMethodDetailEntity,
  lenderSelectedData,
  showDialog,
  setShowDialog,
  onClose,
}: LenderSelectedProps) => {
  const { refreshPaymentMethodEntity } = useBnplPaymentIntent();

  const [shareDialogOpened, setShareDialogOpened] = useState(false);

  if (!lenderSelectedData || !lenderSelectedData.lenderApprovalStatus) {
    return null;
  }

  const setOpened = (showDialog: boolean) => {
    setShowDialog(showDialog);
    if (!showDialog) {
      onClose?.();
      refreshPaymentMethodEntity(paymentMethodDetailEntity);
    }
  };

  const hppId = paymentMethodDetailEntity.checkoutSession.hpp?.id;

  return (
    <>
      <NemuruDialog
        opened={showDialog}
        setOpened={setOpened}
        buttons={
          <>
            {hppId && (
              <StandardButton main onClick={() => setShareDialogOpened(true)}>
                <Text tId={'SHARE_WITH_CUSTOMER'} />
              </StandardButton>
            )}
          </>
        }
      >
        <LenderMessage lenderSelectedData={lenderSelectedData} />
      </NemuruDialog>
      {hppId && (
        <DistributionDialog
          opened={shareDialogOpened}
          setOpened={setShareDialogOpened}
          hppId={hppId}
          agentId={paymentMethodDetailEntity.checkoutSession.agentId}
          checkoutSession={paymentMethodDetailEntity.checkoutSession}
        />
      )}
    </>
  );
};

const LenderMessage = ({ lenderSelectedData }: { lenderSelectedData: LenderSelectedData }) => {
  const { palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();

  if (!lenderSelectedData.lenderApprovalStatus) {
    return null;
  }

  if ([LenderApprovalStatus.YES, LenderApprovalStatus.MAYBE].includes(lenderSelectedData.lenderApprovalStatus)) {
    return (
      <FlexColumn>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          bgcolor={palette.success.light}
          borderRadius="50%"
          width={84}
          height={84}
          mb={3}
        >
          <Icon size={48} color={palette.success.main}>
            check_circle
          </Icon>
        </Box>
        {/*<Box>
            <LenderIcon lenderName={lenderSelectedData.lenderName} size={32} />
          </Box>*/}
        <FlexColumn>
          <Font variant="h3" color="title" display="block">
            <Text strong tId={'BNPL_LENDER_SELECTED_APPROVED_TITLE'} />
          </Font>
          <Font variant="subtitle1" color="textSecondary">
            <Text
              tId={'BNPL_LENDER_SELECTED_APPROVED_DESCRIPTION'}
              dynamicDataTexts={[getTextIdTranslation(lenderSelectedData.lenderName)]}
            />
          </Font>
        </FlexColumn>
      </FlexColumn>
    );
  }

  if (lenderSelectedData.lenderApprovalStatus === LenderApprovalStatus.NO) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" gridGap={8}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor={palette.error.light}
          borderRadius="50%"
          width={80}
          height={80}
          mb={3}
        >
          <Icon size={44} color={palette.error.main}>
            cancel
          </Icon>
        </Box>
        <Font variant="h3" color="title">
          <Text strong tId={'BNPL_LENDER_SELECTED_REJECTED_TITLE'} />
        </Font>
        <Font variant="subtitle1" color="textSecondary">
          <Text tId={'BNPL_LENDER_SELECTED_REJECTED_DESCRIPTION'} />
        </Font>
      </Box>
    );
  }

  return null;
};
